<script>
  import { _l10n, _env } from '../stores.js';
  import { onDestroy, onMount, tick } from 'svelte';
  import { RootStyle, Separator, Link, OverlayLoader } from '@storytel/design-system-web';
  import Centered from '../styling/Centered.svelte';
  import ProviderLogin from '../components/ProviderLogin.svelte';
  import EmailPassword from '../components/EmailPassword.svelte';
  import { getRedirectUrl } from '../utils/getRedirectUrl';
  import { l10n } from '../utils/Utils';
  import { validRedirect } from '../utils/validRedirect';
  import { getLoginData } from '../services/AuthWebComponentsService';
  import { getCustomerId } from '../services/RuntimeWebService';

  export let env, country, countryid, language, forgotpasswordlink, redirecturl, signuplink;

  let isMounted = false,
    showLoader = false,
    fetchData,
    providerInfo = {},
    localization = {},
    fetched,
    currentErrorOrigin = '';

  $: hasAnyProvidersEnabled = isMounted
    ? providerInfo.isAppleEnabled || providerInfo.isGoogleEnabled || providerInfo.isFacebookEnabled
    : false;

  // subscribing to store version of l10n
  _l10n.subscribe((value) => {
    localization = value;
  });

  const unsubscribe = _l10n.subscribe((value) => {
    localization = value;
  });

  // An issue with Svelte and web components makes props unavailable
  // when mounting the component. Therefore we wait one tick
  // https://github.com/sveltejs/svelte/issues/2227
  onMount(async () => {
    await tick();

    if (!env) {
      env = loginapp.env.isProd ? 'prod' : 'test';
    }

    _env.set(env); // set env value in store
    isMounted = true;

    // As long as no working API endpoint is provided, there will be no response (see result in browser)
    fetchData = (async () => {
      [localization, providerInfo] = await getLoginData(env, language, country, countryid);
      _l10n.set(localization);
      fetched = providerInfo && localization;
      return fetched;
    })();
  });

  onDestroy(unsubscribe);

  const afterSuccessfulLogin = async (event) => {
    if (window.dataLayer) {
      // method could contain `.com` on `apple.com` but not on `facebook`, so let's clean it up
      const method = event.detail?.method?.split('.')[0] ?? 'UNKNOWN';
      dataLayer.push({
        event: 'login',
        method,
        user_id: await getCustomerId(),
      });
    }

    if (validRedirect(redirecturl)) {
      window.location = redirecturl;
    } else {
      const redUrl = getRedirectUrl(country, language);
      window.location = redUrl;
    }
  };

  const setCurrentErrorOrigin = (event) => {
    currentErrorOrigin = event.detail.component;
  };

</script>

<svelte:options tag={null} />

<RootStyle>
  {#if isMounted}
    {#await fetchData}
      <div style="height: 450px;">
        <OverlayLoader />
      </div>
    {:then fetched}
      {#if providerInfo && localization}
        {#if hasAnyProvidersEnabled}
          <ProviderLogin
            {env}
            {country}
            {language}
            {providerInfo}
            {currentErrorOrigin}
            bind:showLoader
            on:loginSuccess={afterSuccessfulLogin}
            on:error={setCurrentErrorOrigin}
          />
        {/if}

        {#if hasAnyProvidersEnabled}
          <div>
            <Separator text={l10n('login.orWith')} />
          </div>
        {/if}

        <EmailPassword
          {country}
          {language}
          {env}
          forgotPasswordLink={forgotpasswordlink}
          {currentErrorOrigin}
          bind:showLoader
          on:loginSuccess={afterSuccessfulLogin}
          on:error={setCurrentErrorOrigin}
        />

        <Centered>
          <p>
            {l10n('login.noAccount.signup.text')}
            <Link noUnderline="true" orange="true" href={signuplink}>
              {l10n('login.noAccount.signUp.link')}
            </Link>
          </p>
        </Centered>

        {#if showLoader}
          <OverlayLoader />
        {/if}
      {:else}
        <h1>Oops!</h1>
        <p>Something went wrong and the data could not be loaded.</p>
        {console.error('External data could not be loaded.')}
      {/if}
    {:catch error}
      {console.warn(error)}
    {/await}
  {/if}
</RootStyle>
